const padUtc = value => String(value).padStart(2, '0');

const UTC_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const toUtcDate = value => {
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  return new Date(value);
};

export const formatInUtc = (value, pattern) => {
  const date = toUtcDate(value);
  const tokens = {
    yyyy: String(date.getUTCFullYear()),
    dd: padUtc(date.getUTCDate()),
    d: String(date.getUTCDate()),
    MMM: UTC_MONTHS[date.getUTCMonth()],
    HH: padUtc(date.getUTCHours()),
    mm: padUtc(date.getUTCMinutes()),
  };
  return pattern.replace(/yyyy|MMM|dd|d|HH|mm/g, token => tokens[token]);
};

export const formatUnixUtcDateTime = unixSeconds => {
  const date = new Date(Number(unixSeconds) * 1000);
  return [
    date.getUTCFullYear(),
    '-',
    padUtc(date.getUTCMonth() + 1),
    '-',
    padUtc(date.getUTCDate()),
    'T',
    padUtc(date.getUTCHours()),
    ':',
    padUtc(date.getUTCMinutes()),
    ':',
    padUtc(date.getUTCSeconds()),
    'Z',
  ].join('');
};

export const unixRangeForUtcDateLabel = (dateLabel, granularity = 'day') => {
  const match = String(dateLabel || '').match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}))?/
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const hour = match[4] != null ? Number(match[4]) : 0;

  if (granularity === 'hour') {
    const from = Math.floor(Date.UTC(year, month, day, hour) / 1000);
    return { from, to: from + 3599 };
  }

  const from = Math.floor(Date.UTC(year, month, day) / 1000);
  const span = granularity === 'week' ? 7 * 86400 : 86400;
  return { from, to: from + span - 1 };
};

// DatePicker cells are local calendar dates (Y-M-D). Reports treat that Y-M-D as a UTC day,
// so picking Aug 26 always maps to 00:00:00Z–23:59:59Z. Store dates at UTC noon so local
// midnight in UTC+ no longer looks like the previous UTC day to getUTC* / formatInUtc.
const calendarDayParts = date => {
  const d = new Date(date);
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
  };
};

export const startOfUtcPickerDay = date => {
  const { year, month, day } = calendarDayParts(date);
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
};

export const utcCalendarToday = (value = new Date()) => {
  const d = toUtcDate(value);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0)
  );
};

export const unixToUtcCalendarDate = unixSeconds =>
  utcCalendarToday(Number(unixSeconds) * 1000);

export const getUnixStartOfUtcDay = date => {
  const d = startOfUtcPickerDay(date);
  return Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000
  );
};

export const getUnixEndOfUtcDay = date => {
  const d = startOfUtcPickerDay(date);
  return Math.floor(
    (Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) - 1) /
      1000
  );
};

export const isSameUtcDay = (left, right = new Date()) => {
  const a = new Date(left);
  const b = new Date(right);
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
};

export const rangeIncludesCurrentUtcDay = (fromUnix, toUnix) => {
  if (!fromUnix || !toUnix) return false;
  const now = Math.floor(Date.now() / 1000);
  return now >= Number(fromUnix) && now <= Number(toUnix);
};
